import { isEmpty, replace } from "lodash";
import { useRouter } from "next/router";
import React, { ReactNode } from "react";
import { useCompanies } from "../../api/useCompanies";
import { useLocations } from "../../api/useLocations";
import usePermissions from "../../api/usePermissions";
import { useSettings } from "../../api/useSettings";
import { ANY_LOCATION_ID, PERMISSIONS } from "../../constants";
import { SettingProduct } from "../../models";

interface PermissionsGateProps {
    children?: ReactNode;
    permissions?: string[];
    globalPermissions?: string[];
    productsToCheck?: SettingProduct[];
    unauthorizedComponent?: ReactNode;
    onUnauthorized?: VoidFunction;
    checkAnyLocation?: boolean;
    doRerenderOnNavigation?: boolean;
}

const PermissionsGate = (props: PermissionsGateProps) => {
    const {
        children,
        globalPermissions = [PERMISSIONS.BETTERSPACE_ADMIN.READ], // always check if betterspace admin if not specified otherwise
        permissions = [],
        productsToCheck = [],
        unauthorizedComponent = null,
        onUnauthorized,
        checkAnyLocation = false,
        doRerenderOnNavigation = false, // Force rerendering when route changes due to shallow routing issues in Next.js (see https://github.com/vercel/next.js/issues/9992)
    } = props;

    const router = useRouter();
    const { companyId, locationId } = router.query as {
        companyId?: string;
        locationId?: string;
    };
    const { asPath, pathname } = router;

    const {
        hasPermissions,
        hasLocationInPermissions,
        hasCompanyInPermissions,
        isLoadingPermissions,
        getCompanyPermissions,
    } = usePermissions();
    const { companies } = useCompanies();
    const { locations } = useLocations({ companyId });

    // settings contain which products + modules are allowed on the company/location
    const { isLoadingSettings, hasProductModules } = useSettings({
        companyId: isEmpty(productsToCheck) ? undefined : companyId,
        locationId,
    });

    // Wait for company/location settings and permissions before rendering anything
    if (isLoadingSettings || isLoadingPermissions) {
        return null;
    }

    // If has wrong companyId in path, then re-route to the allowed one
    if (
        !hasPermissions() &&
        companyId &&
        !hasCompanyInPermissions(companyId) &&
        companies?.[0]
    ) {
        router.push({
            pathname,
            query: {
                companyId: companies?.[0].id,
                ...(locationId &&
                    locations?.[0] && { locationId: locations[0].id }),
            },
        });
        return null;
    }

    // If has wrong locationId in path, then re-route to the allowed one
    if (
        // no betterspace admin
        !hasPermissions() &&
        // checks for users only with location permissions
        locationId &&
        !hasLocationInPermissions(companyId, locationId) &&
        locations?.[0] &&
        // if company permissions exists -> user is company admin -> dont route
        isEmpty(getCompanyPermissions(companyId))
    ) {
        router.push({
            pathname,
            query: {
                companyId,
                locationId: locations[0].id,
            },
        });
        return null;
    }

    const isPermissionGranted = hasPermissions({
        globalPermissions,
        permissions,
        companyId,
        locationId: checkAnyLocation ? ANY_LOCATION_ID : locationId,
    });

    if (!isPermissionGranted || !hasProductModules(productsToCheck)) {
        if (onUnauthorized) {
            onUnauthorized();
            return null;
        }
        return <>{unauthorizedComponent}</>;
    }

    if (doRerenderOnNavigation) {
        // Create key from URL path
        // "^\/" matches the first "/" -> replace with nothing
        // "\/+" matches "/" after the first occurance -> replace with "-"
        const formattedKey = replace(asPath, /^\/|\/+/g, (_, index) =>
            index === 0 ? "" : "-"
        );
        return <React.Fragment key={formattedKey}>{children}</React.Fragment>;
    }

    return <>{children}</>;
};

export default PermissionsGate;
